import { useState, useEffect } from 'react'
import { usePortalClient } from '../hooks/usePortalClient'
import { supabase } from '../../../shared/lib/supabase'
import benchmarkData from '../../../shared/data/industryBenchmarks.json'

const TIER_STYLES = {
  critical: { classes: 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800', label: 'Critical' },
  high: { classes: 'bg-orange-50 text-orange-800 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800', label: 'High' },
  moderate: { classes: 'bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800', label: 'Moderate' },
  low: { classes: 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800', label: 'Low' }
}

function PortalSecurity() {
  const { client, clientId, loading: clientLoading } = usePortalClient()
  const [systems, setSystems] = useState([])
  const [loading, setLoading] = useState(true)

  // Fetch critical systems
  useEffect(() => {
    if (!clientId) return
    const fetchSystems = async () => {
      const { data } = await supabase
        .from('client_critical_systems')
        .select('*')
        .eq('client_id', clientId)
        .order('criticality_tier')
      setSystems(data || [])
      setLoading(false)
    }
    fetchSystems()
  }, [clientId])

  // Get frameworks for this client's industry
  const industryData = client?.industry ? benchmarkData.benchmarks?.[client.industry] : null
  const frameworks = industryData?.keyFrameworks || []

  // Risk appetite from latest assessment
  const completedAssessments = (client?.assessments || []).filter(a => a.status === 'completed')
  const latestScores = completedAssessments[0]?.scores

  if (clientLoading || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Risk Profile Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Risk Profile</h2>
        {latestScores?.riskAppetite ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(latestScores.riskAppetite.domains || {}).slice(0, 4).map(([domain, data]) => (
              <div key={domain} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium uppercase tracking-wider mb-1 text-gray-500 dark:text-gray-400">{domain}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{data.label || data.level || '—'}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Risk profile data will be available after your assessment is completed.
          </p>
        )}
      </div>

      {/* Critical Systems */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Business-Critical Systems</h2>
        {systems.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            No critical systems have been documented yet. Your advisor will work with you to catalog your key business systems.
          </p>
        ) : (
          <div className="space-y-2">
            {systems.map(sys => {
              const tier = TIER_STYLES[sys.criticality_tier] || TIER_STYLES.moderate
              return (
                <div key={sys.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{sys.name}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{sys.business_function}</p>
                    {sys.business_impact && (
                      <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">Impact: {sys.business_impact}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${tier.classes}`}>
                    {tier.label}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Applicable Frameworks */}
      {frameworks.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Applicable Frameworks</h2>
          <div className="flex flex-wrap gap-2">
            {frameworks.map(fw => (
              <span key={fw} className="text-xs font-medium px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800">
                {fw}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default PortalSecurity
